import {
  useEffect, useRef, useState,
  type MouseEvent as ReactMouseEvent, type ReactNode, type RefObject,
} from 'react'
import { readSet, writeSet } from './prefs'

/** The parts of a sheet screen that more than one sheet needs.
 *
 *  DELIBERATELY NOT A `<Sheet>` COMPONENT. A generic table that tries to own
 *  the toolbar, the fetch, the footer and the row is worse than two honest
 *  copies: every screen then needs an escape hatch, the props grow to cover
 *  each one, and the thing nobody can read is the one every screen depends on.
 *  What is here is the four mechanisms where a COPY would silently lose a bug
 *  that has already been paid for once — the column catalogue's ordering rule,
 *  the dialog's single exit guard, `inert`, and focus return. The table markup
 *  itself is a dozen lines per screen and is better read in place.
 *
 *  The phone book is the reference implementation; anything that looks odd
 *  here is odd because of something that happened there.
 */

// ---------------------------------------------------------------------------
// Columns
// ---------------------------------------------------------------------------

/** One column of a sheet. `T` is whatever a row is. */
export type Column<T> = {
  key: string
  header: string
  /** Cannot be hidden. A sheet's identity column, and there may be more than
   *  one — `ColumnMenu` counts them rather than assuming a number. */
  fixed?: boolean
  cell: (row: T) => ReactNode
  /** A `table.sheet` cell class: nm, num, addr, when, clip, muted, chan. */
  className?: string
  /** What this column sorts on, for a sheet whose headers are sort buttons.
   *  `null` is "nothing in this column", and such rows sit at the bottom
   *  whichever way the column is turned. Strings compare as a person reads
   *  them (case does not matter, numbers in counting order); numbers compare
   *  as numbers, so a column whose first press should show newest or most
   *  first hands back a negated value. Absent: the header is a plain label. */
  sortKey?: (row: T) => string | number | null
}

/** A value, or an em dash. */
export function dash(value: string | null | undefined): ReactNode {
  return value ? value : <span className="muted">—</span>
}

/** A TIMESTAMP — `created_at`, `updated_at` — as a short local date.
 *
 *  Correct for a `timestamptz`, which serialises with an offset, and WRONG for
 *  a bare `date`. Use `calendarDay` for those. */
export function day(value: string | null | undefined): ReactNode {
  if (!value) return <span className="muted">—</span>
  return new Date(value).toLocaleDateString([], { day: '2-digit', month: 'short' })
}

/** A bare `date` as a real local Date, or null if it is not one.
 *
 *  THE ONE PARSER, and both renderers below go through it. A `date` serialises
 *  as "1985-03-04" with no offset, and `new Date` reads a bare ISO date as UTC
 *  MIDNIGHT — which rendered in any timezone west of Greenwich is the day
 *  before. A joining date quietly one day early is the kind of wrong nobody
 *  reports and nobody can explain later, and a second copy of this three-line
 *  regex is how one of the two renderers comes to have that bug and the other
 *  not.
 */
function calendarParts(value: string): Date | null {
  const parts = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
  if (!parts) return null
  // Constructed from the parts, which is local midnight by definition.
  return new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]))
}

/** A bare DATE column — `members.joined_on` — as a short local date.
 *
 *  NO YEAR, which is right for a dense sheet where the column is one of
 *  fifteen. Use `calendarDate` where the year is the point.
 */
export function calendarDay(value: string | null | undefined): ReactNode {
  if (!value) return <span className="muted">—</span>
  const local = calendarParts(value)
  if (!local) return day(value)
  return local.toLocaleDateString([], { day: '2-digit', month: 'short' })
}

/** The same bare DATE, WITH ITS YEAR.
 *
 *  A SEPARATE RENDERER RATHER THAN A FLAG, because which one a field wants is
 *  a property of the field. `calendarDay` drops the year to keep a sheet
 *  readable; on an expiry date the year IS the fact — "04 Mar" says nothing
 *  about whether a certificate lapsed last spring or lapses next one, which is
 *  the entire question an expiry date is on the record to answer. Dates of
 *  birth are the same: the year is most of what a date of birth is.
 */
export function calendarDate(value: string | null | undefined): ReactNode {
  if (!value) return <span className="muted">—</span>
  const local = calendarParts(value)
  if (!local) return day(value)
  return local.toLocaleDateString(
    [], { day: '2-digit', month: 'short', year: 'numeric' })
}

/** The remembered column choice for ONE sheet.
 *
 *  THE CATALOGUE DECIDES ORDER, THE PREFERENCE DECIDES MEMBERSHIP, and that
 *  asymmetry is the whole reason this is a hook rather than three lines copied
 *  onto each screen. `prefs.readSet` returns the STORED array, which is in
 *  whatever order the boxes happened to be ticked — render from it and the
 *  picker becomes a column-REORDER control by accident.
 *
 *  `prefKey` MUST BE UNIQUE PER SHEET, and it is the single most dangerous
 *  argument here. `writeSet` is unfiltered: two sheets sharing a key would
 *  read each other's list, filter it down to the keys their own catalogue
 *  happens to share — `phone`, `email`, `created_at` are common to several —
 *  render a plausible but wrong sheet, and then write that filtered list back
 *  on the first checkbox touched, destroying the other screen's choice for
 *  good. TypeScript sees a string and nothing fails.
 */
export function useColumns<T>(
  prefKey: string,
  catalogue: readonly Column<T>[],
  fallback: readonly string[],
): { shown: Column<T>[]; visible: string[]; choose: (next: string[]) => void } {
  const toggleable = catalogue.filter((c) => !c.fixed).map((c) => c.key)
  const [visible, setVisible] = useState<string[]>(
    () => readSet(prefKey, toggleable, fallback),
  )
  const shown = catalogue.filter((c) => c.fixed || visible.includes(c.key))

  function choose(next: string[]) {
    setVisible(next)
    writeSet(prefKey, next)
  }
  return { shown: [...shown], visible, choose }
}

export function ColumnMenu<T>({
  catalogue, visible, fallback, resetLabel, onChange,
}: {
  catalogue: readonly Column<T>[]
  visible: string[]
  fallback: readonly string[]
  /** "Back to the usual six" — a count, and it differs per sheet. */
  resetLabel: string
  onChange: (next: string[]) => void
}) {
  const [open, setOpen] = useState(false)
  const wrap = useRef<HTMLDivElement>(null)

  // Closes on a click anywhere else and on Escape. A menu that only closes by
  // clicking its own button is a menu people leave open over the thing they
  // came here to read.
  useEffect(() => {
    if (!open) return
    function away(e: globalThis.MouseEvent) {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false)
    }
    function key(e: KeyboardEvent) { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', away)
    window.addEventListener('keydown', key)
    return () => {
      document.removeEventListener('mousedown', away)
      window.removeEventListener('keydown', key)
    }
  }, [open])

  function toggle(key: string) {
    onChange(visible.includes(key)
      ? visible.filter((k) => k !== key)
      : [...visible, key])
  }

  // COUNTED, NOT ASSUMED. This read `visible.length + 1`, which is a hardcoded
  // "there is exactly one fixed column" — true of the phone book and false of
  // any sheet with two.
  const fixed = catalogue.filter((c) => c.fixed).length

  return (
    <div className="sheet-colwrap" ref={wrap}>
      <button
        className="ghost tiny nogrow"
        type="button"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen(!open)}
      >
        Columns · {visible.length + fixed}
      </button>
      {open && (
        <div className="sheet-colmenu" role="group" aria-label="Columns to show">
          {catalogue.map((c) => (
            <label className="sheet-colopt" key={c.key}>
              <input
                type="checkbox"
                checked={!!c.fixed || visible.includes(c.key)}
                disabled={c.fixed}
                onChange={() => toggle(c.key)}
              />
              <span className="sheet-colname">{c.header}</span>
              {c.fixed && <span className="lbl">always</span>}
            </label>
          ))}
          <button
            className="ghost tiny nogrow sheet-colreset"
            type="button"
            onClick={() => onChange([...fallback])}
          >
            {resetLabel}
          </button>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Dialogs
// ---------------------------------------------------------------------------

/** Four exits, one guard, and a backdrop that needs the press AND the release.
 *
 *  `confirmMessage()` returns the sentence to ask, or null when there is
 *  nothing to lose. It is called at the moment of leaving, not remembered, so
 *  it sees the current state.
 *
 *  THE BACKDROP TEST IS TWO EVENTS ON PURPOSE. Checking only the click's
 *  target looks right and is not: press inside a textarea, drag to select,
 *  release past the dialog's edge, and the browser fires `click` on the
 *  nearest common ancestor of the two — which IS the backdrop. So selecting a
 *  sentence threw the record away. Both the press and the release have to land
 *  on the backdrop.
 */
export function useDialogGuard(
  confirmMessage: () => string | null,
  onClose: () => void,
): {
  leave: () => void
  backdrop: {
    onMouseDown: (e: ReactMouseEvent) => void
    onClick: (e: ReactMouseEvent) => void
  }
} {
  const downOnBackdrop = useRef(false)

  function leave() {
    const ask = confirmMessage()
    if (ask && !confirm(ask)) return
    onClose()
  }

  // Escape, through the same guard. Registered on every render so the closure
  // sees the current state — the pattern RadioBar already uses. No
  // stopPropagation: this is a `window` listener, and stopping propagation on
  // an event already at the window does not reach a sibling listener on the
  // same target.
  useEffect(() => {
    function key(e: KeyboardEvent) {
      if (e.key === 'Escape') leave()
    }
    window.addEventListener('keydown', key)
    return () => window.removeEventListener('keydown', key)
  })

  return {
    leave,
    backdrop: {
      onMouseDown: (e: ReactMouseEvent) => {
        downOnBackdrop.current = e.target === e.currentTarget
      },
      onClick: (e: ReactMouseEvent) => {
        if (downOnBackdrop.current && e.target === e.currentTarget) leave()
      },
    },
  }
}

/** `inert` on everything a dialog covers, while it is open.
 *
 *  IMPERATIVE, NOT A PROP. React 18 renders an unknown boolean attribute as
 *  the string "false", and an `inert="false"` attribute is PRESENT — so
 *  `inert={dialogOpen}` produces a permanently dead screen the moment the
 *  first dialog closes.
 *
 *  It matters because sheet rows are focusable: without this, Tab walks out of
 *  an open dialog into the rows behind it, where Enter opens a different
 *  record and discards the first with no prompt.
 */
export function useInertWhile(ref: RefObject<HTMLElement | null>, on: boolean): void {
  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (on) el.setAttribute('inert', '')
    else el.removeAttribute('inert')
  }, [ref, on])
}

/** Put focus back where it came from when a dialog unmounts.
 *
 *  Closing with Escape otherwise drops focus onto `<body>`, and the next Tab
 *  restarts at the top of the document — which on a sheet means walking the
 *  whole thing again to get back to the row you were on.
 */
export function useReturnFocus(): void {
  useEffect(() => {
    const from = document.activeElement as HTMLElement | null
    return () => { if (from && from.isConnected) from.focus() }
  }, [])
}
